import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Info } from 'lucide-react';
import {
  buildRelevanceGraphView,
  buildFullTaxonomyGraphView,
  toggleInArray,
  type Incident,
  type Insight,
  type GraphNode,
  type GraphEdgeView,
} from '@duty/shared';
import { useIncidentStore } from '@/incident/store';

interface SimNode extends GraphNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
}

const NODE_RADIUS: Record<GraphNode['kind'], number> = {
  incident: 30,
  hub: 17,
  threat: 11,
  action: 11,
  support: 11,
  'field-option': 10,
  capability: 18,
  insight: 13,
  stage: 12,
  type: 16,
  category: 14,
};

const EDGE_REST_LENGTH: Record<GraphEdgeView['kind'], number> = {
  hub: 140,
  member: 78,
  surface: 100,
  demote: 100,
  capability: 95,
  insight: 115,
  'type-member': 90,
};

/** Sci-fi palette — a dedicated dark/neon rendering regardless of the app's light/dark theme, matching the "movie UI" brief; this view is occasional and full-screen, not part of the everyday chrome. */
const COLORS: Record<GraphNode['kind'], { active: string; inactive: string }> = {
  incident: { active: '#7dd3fc', inactive: '#7dd3fc' },
  hub: { active: '#64748b', inactive: '#475569' },
  threat: { active: '#f87171', inactive: '#4b5563' },
  action: { active: '#4ade80', inactive: '#4b5563' },
  support: { active: '#facc15', inactive: '#4b5563' },
  'field-option': { active: '#c084fc', inactive: '#4b5563' },
  capability: { active: '#38bdf8', inactive: '#38bdf8' },
  insight: { active: '#f472b6', inactive: '#f472b6' },
  stage: { active: '#fb923c', inactive: '#fb923c' },
  type: { active: '#a78bfa', inactive: '#a78bfa' },
  category: { active: '#2dd4bf', inactive: '#2dd4bf' },
};

const EDGE_COLOR: Record<GraphEdgeView['kind'], string> = {
  hub: 'rgba(148,163,184,0.25)',
  member: 'rgba(148,163,184,0.2)',
  surface: 'rgba(56,189,248,0.85)',
  demote: 'rgba(148,163,184,0.35)',
  capability: 'rgba(56,189,248,0.5)',
  insight: 'rgba(244,114,182,0.55)',
  'type-member': 'rgba(167,139,250,0.3)',
};

/** Which Itasc field a field-option node's group hub corresponds to, and how to toggle it. */
function fieldKeyForGroup(group: string): 'showing' | 'strategy' | 'fireStatus' | undefined {
  if (group === 'hub:showing') return 'showing';
  if (group === 'hub:strategy') return 'strategy';
  if (group === 'hub:firestatus') return 'fireStatus';
  return undefined;
}

/**
 * Full-screen, interactive force-directed visualisation of the ITASC
 * relevance graph (see `shared/src/relevance-graph-view.ts`) — a sci-fi
 * "why is this being recommended" view, not a new source of truth: clicking
 * a tag/field node dispatches the exact same `update-itasc` action
 * `ItascCapture`'s chips do, so the two stay in sync automatically. Purely
 * additive — this view never fabricates a fact, it only visualises the
 * live, already-deterministic ITASC + relevance-graph + insight state.
 */
export function RelevanceGraphView({ incident, insights, onClose }: { incident: Incident; insights: Insight[]; onClose: () => void }) {
  const { dispatch, config } = useIncidentStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const simNodesRef = useRef<Map<string, SimNode>>(new Map());
  const edgesRef = useRef<GraphEdgeView[]>([]);
  const hoverIdRef = useRef<string | null>(null);
  const [, forceRerender] = useState(0);
  const [mode, setMode] = useState<'incident' | 'taxonomy'>('incident');

  // "This incident" scopes to the active incident's type(s)/state (see buildRelevanceGraphView's own
  // doc comment for why it's deliberately filtered); "Full taxonomy" is the complete, incident-agnostic
  // map of every incident type/stage/tag/relevance-edge (buildFullTaxonomyGraphView) — the "not filtered
  // by incident type, the logical mapping of all possible activities" view requested alongside
  // docs/doctrine-research/incident-lifecycle-and-strategy.md.
  const graph = useMemo(
    () => (mode === 'incident' ? buildRelevanceGraphView(incident, insights, config) : buildFullTaxonomyGraphView()),
    [mode, incident, insights, config],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Seed/merge simulation node state: existing nodes keep their live position (so the layout doesn't
  // jump every time ITASC changes), new nodes spawn near their first linked neighbour (or the centre).
  useEffect(() => {
    const canvas = canvasRef.current;
    const w = canvas?.clientWidth ?? 800;
    const h = canvas?.clientHeight ?? 600;
    const prev = simNodesRef.current;
    const next = new Map<string, SimNode>();
    for (const n of graph.nodes) {
      const existing = prev.get(n.id);
      if (existing) {
        next.set(n.id, { ...existing, ...n, radius: NODE_RADIUS[n.kind] });
      } else {
        const neighbourEdge = graph.edges.find((e) => e.target === n.id || e.source === n.id);
        const anchor = neighbourEdge ? next.get(neighbourEdge.source) ?? next.get(neighbourEdge.target) : undefined;
        const baseX = anchor?.x ?? w / 2;
        const baseY = anchor?.y ?? h / 2;
        next.set(n.id, {
          ...n,
          radius: NODE_RADIUS[n.kind],
          x: baseX + (Math.random() - 0.5) * 60,
          y: baseY + (Math.random() - 0.5) * 60,
          vx: 0,
          vy: 0,
        });
      }
    }
    simNodesRef.current = next;
    edgesRef.current = graph.edges;
  }, [graph]);

  // Force simulation + render loop.
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    let raf = 0;
    let running = true;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = container.clientWidth * dpr;
      canvas.height = container.clientHeight * dpr;
      canvas.style.width = `${container.clientWidth}px`;
      canvas.style.height = `${container.clientHeight}px`;
      const ctx = canvas.getContext('2d');
      ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const tick = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      const nodes = [...simNodesRef.current.values()];
      const edges = edgesRef.current;

      // Repulsion — every pair pushes apart (bounded to keep small graphs from flying off-screen).
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i]!;
          const b = nodes[j]!;
          let dx = b.x - a.x;
          let dy = b.y - a.y;
          let distSq = dx * dx + dy * dy;
          if (distSq < 1) {
            dx = Math.random() - 0.5;
            dy = Math.random() - 0.5;
            distSq = 1;
          }
          const dist = Math.sqrt(distSq);
          const force = Math.min(2200 / distSq, 6);
          const fx = (dx / dist) * force;
          const fy = (dy / dist) * force;
          a.vx -= fx;
          a.vy -= fy;
          b.vx += fx;
          b.vy += fy;
        }
      }

      // Spring attraction along edges toward a rest length.
      for (const e of edges) {
        const a = simNodesRef.current.get(e.source);
        const b = simNodesRef.current.get(e.target);
        if (!a || !b) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.max(1, Math.sqrt(dx * dx + dy * dy));
        const rest = EDGE_REST_LENGTH[e.kind];
        const k = e.kind === 'hub' || e.kind === 'member' ? 0.02 : 0.012;
        const force = (dist - rest) * k;
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        a.vx += fx;
        a.vy += fy;
        b.vx -= fx;
        b.vy -= fy;
      }

      // Weak centre gravity + damping + integrate + clamp to viewport.
      for (const n of nodes) {
        n.vx += (w / 2 - n.x) * 0.0006;
        n.vy += (h / 2 - n.y) * 0.0006;
        n.vx *= 0.86;
        n.vy *= 0.86;
        n.x += Math.max(-8, Math.min(8, n.vx));
        n.y += Math.max(-8, Math.min(8, n.vy));
        n.x = Math.max(n.radius, Math.min(w - n.radius, n.x));
        n.y = Math.max(n.radius, Math.min(h - n.radius, n.y));
      }

      draw(canvas, nodes, edges, w, h, hoverIdRef.current);
      if (running) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [graph]);

  const nodeAt = (clientX: number, clientY: number): SimNode | undefined => {
    const container = containerRef.current;
    if (!container) return undefined;
    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    let best: SimNode | undefined;
    let bestDist = Infinity;
    for (const n of simNodesRef.current.values()) {
      const dx = n.x - x;
      const dy = n.y - y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= n.radius + 4 && dist < bestDist) {
        best = n;
        bestDist = dist;
      }
    }
    return best;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (mode !== 'incident') return; // taxonomy nodes aren't scoped to this incident's ITASC — view-only
    const node = nodeAt(e.clientX, e.clientY);
    if (!node) return;
    const { itasc } = incident;
    if (node.kind === 'threat') {
      const tag = node.id.slice('threat:'.length);
      dispatch({ type: 'update-itasc', patch: { threats: { selected: toggleInArray(itasc.threats.selected, tag as never) } } });
    } else if (node.kind === 'action') {
      const tag = node.id.slice('action:'.length);
      dispatch({ type: 'update-itasc', patch: { actions: { selected: toggleInArray(itasc.actions.selected, tag as never) } } });
    } else if (node.kind === 'support') {
      const tag = node.id.slice('support:'.length);
      dispatch({ type: 'update-itasc', patch: { support: { requested: toggleInArray(itasc.support.requested, tag as never) } } });
    } else if (node.kind === 'field-option' && node.group) {
      const field = fieldKeyForGroup(node.group);
      if (!field) return;
      const value = node.id.slice(node.group.length + 1);
      const current = field === 'showing' ? itasc.incident.showing : field === 'fireStatus' ? itasc.incident.fireStatus : itasc.actions.strategy;
      const nextValue = current === value ? undefined : value;
      if (field === 'showing') dispatch({ type: 'update-itasc', patch: { incident: { showing: nextValue as never } } });
      else if (field === 'fireStatus') dispatch({ type: 'update-itasc', patch: { incident: { fireStatus: nextValue as never } } });
      else dispatch({ type: 'update-itasc', patch: { actions: { strategy: nextValue as never } } });
    }
  };

  const handleMove = (e: React.MouseEvent) => {
    const node = nodeAt(e.clientX, e.clientY);
    const id = node?.id ?? null;
    if (hoverIdRef.current !== id) {
      hoverIdRef.current = id;
      if (containerRef.current) containerRef.current.style.cursor = mode === 'incident' && node && isInteractive(node) ? 'pointer' : 'default';
      forceRerender((v) => v + 1);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (mode !== 'incident') return; // taxonomy nodes aren't scoped to this incident's ITASC — view-only
    const touch = e.touches[0];
    if (!touch) return;
    const node = nodeAt(touch.clientX, touch.clientY);
    if (!node) return;
    const { itasc } = incident;
    if (node.kind === 'threat') {
      const tag = node.id.slice('threat:'.length);
      dispatch({ type: 'update-itasc', patch: { threats: { selected: toggleInArray(itasc.threats.selected, tag as never) } } });
    } else if (node.kind === 'action') {
      const tag = node.id.slice('action:'.length);
      dispatch({ type: 'update-itasc', patch: { actions: { selected: toggleInArray(itasc.actions.selected, tag as never) } } });
    } else if (node.kind === 'support') {
      const tag = node.id.slice('support:'.length);
      dispatch({ type: 'update-itasc', patch: { support: { requested: toggleInArray(itasc.support.requested, tag as never) } } });
    } else if (node.kind === 'field-option' && node.group) {
      const field = fieldKeyForGroup(node.group);
      if (!field) return;
      const value = node.id.slice(node.group.length + 1);
      const current = field === 'showing' ? itasc.incident.showing : field === 'fireStatus' ? itasc.incident.fireStatus : itasc.actions.strategy;
      const nextValue = current === value ? undefined : value;
      if (field === 'showing') dispatch({ type: 'update-itasc', patch: { incident: { showing: nextValue as never } } });
      else if (field === 'fireStatus') dispatch({ type: 'update-itasc', patch: { incident: { fireStatus: nextValue as never } } });
      else dispatch({ type: 'update-itasc', patch: { actions: { strategy: nextValue as never } } });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    const node = nodeAt(touch.clientX, touch.clientY);
    const id = node?.id ?? null;
    if (hoverIdRef.current !== id) {
      hoverIdRef.current = id;
      forceRerender((v) => v + 1);
    }
  };

  const hovered = hoverIdRef.current ? simNodesRef.current.get(hoverIdRef.current) : undefined;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#05070b]" role="dialog" aria-modal="true" aria-label="Relevance graph">
      <div className="flex items-center justify-between border-b border-white/10 bg-black/40 px-4 py-2.5">
        <div>
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-100">Relevance graph</h2>
          <p className="text-xs text-slate-400">
            {mode === 'incident'
              ? "Live, deterministic view of what's in play — click a tag or option to toggle it, same as the ITASC capture panel."
              : 'Complete taxonomy — every incident type, lifecycle stage, tag and relevance relationship, unfiltered. View only.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div role="group" aria-label="Graph scope" className="flex overflow-hidden rounded-md border border-white/15 text-xs">
            <button
              onClick={() => setMode('incident')}
              aria-pressed={mode === 'incident'}
              className={`px-2.5 py-1 ${mode === 'incident' ? 'bg-sky-400/20 text-sky-200' : 'bg-white/5 text-slate-400 hover:text-white'}`}
            >
              This incident
            </button>
            <button
              onClick={() => setMode('taxonomy')}
              aria-pressed={mode === 'taxonomy'}
              className={`px-2.5 py-1 ${mode === 'taxonomy' ? 'bg-sky-400/20 text-sky-200' : 'bg-white/5 text-slate-400 hover:text-white'}`}
            >
              Full taxonomy
            </button>
          </div>
          <button
            onClick={onClose}
            className="rounded-md border border-white/15 bg-white/5 p-1.5 text-slate-300 hover:text-white"
            aria-label="Close relevance graph"
          >
            <X size={16} />
          </button>
        </div>
      </div>
      <div ref={containerRef} className="relative min-h-0 flex-1">
        <canvas
          ref={canvasRef}
          id="relevance-graph-canvas"
          className="absolute inset-0 h-full w-full"
          onClick={handleClick}
          onMouseMove={handleMove}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
        />
        {hovered && (
          <div className="pointer-events-none absolute left-3 top-3 max-w-xs rounded-md border border-white/10 bg-black/70 px-2.5 py-1.5 text-xs text-slate-200">
            <div className="font-semibold">{hovered.label}</div>
            {hovered.detail && <div className="text-slate-400">{hovered.detail}</div>}
          </div>
        )}
        <div className="pointer-events-none absolute bottom-3 left-3 flex items-start gap-1.5 rounded-md border border-white/10 bg-black/50 px-2.5 py-1.5 text-[10px] leading-relaxed text-slate-400">
          <Info size={11} className="mt-0.5 shrink-0" />
          <span>
            {mode === 'incident'
              ? 'Red = threats, green = actions, amber = support, purple = single-select options (mutually exclusive within a group), blue = capability, pink = open insights. Bright = active/selected.'
              : 'Red = threats, green = actions, amber = support, orange = lifecycle stages, violet = incident types, teal = support categories.'}
          </span>
        </div>
      </div>
    </div>
  );
}

function isInteractive(n: GraphNode): boolean {
  return n.kind === 'threat' || n.kind === 'action' || n.kind === 'support' || n.kind === 'field-option';
}

function draw(canvas: HTMLCanvasElement, nodes: SimNode[], edges: GraphEdgeView[], w: number, h: number, hoverId: string | null): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, w, h);

  const grad = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) / 1.2);
  grad.addColorStop(0, '#0b1220');
  grad.addColorStop(1, '#05070b');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const t = performance.now() / 1000;

  for (const e of edges) {
    const a = byId.get(e.source);
    const b = byId.get(e.target);
    if (!a || !b) continue;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.strokeStyle = EDGE_COLOR[e.kind];
    ctx.lineWidth = e.kind === 'surface' || e.kind === 'insight' ? 1.6 : 1;
    if (e.kind === 'demote' || e.kind === 'insight') ctx.setLineDash([4, 4]);
    else ctx.setLineDash([]);
    ctx.stroke();
  }
  ctx.setLineDash([]);

  for (const n of nodes) {
    const palette = COLORS[n.kind];
    const isOn = n.active && !n.excluded;
    const color = isOn ? palette.active : palette.inactive;
    const pulse = isOn ? 1 + 0.12 * Math.sin(t * 2.4 + n.x * 0.01) : 1;
    const r = n.radius * pulse;

    ctx.beginPath();
    ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
    ctx.fillStyle = isOn ? color : 'rgba(30,41,59,0.85)';
    if (isOn) {
      ctx.shadowColor = color;
      ctx.shadowBlur = n.id === hoverId ? 26 : 16;
    } else {
      ctx.shadowBlur = 0;
    }
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.lineWidth = n.excluded ? 1.5 : 1;
    ctx.strokeStyle = n.excluded ? 'rgba(148,163,184,0.6)' : isOn ? color : 'rgba(100,116,139,0.6)';
    if (n.excluded) ctx.setLineDash([3, 3]);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.font = n.kind === 'incident' ? '600 12px system-ui, sans-serif' : '500 10px system-ui, sans-serif';
    ctx.fillStyle = isOn ? '#e2e8f0' : 'rgba(148,163,184,0.75)';
    ctx.textAlign = 'center';
    ctx.fillText(n.label, n.x, n.y + r + 12);
  }
}
